/**
 * =============================================================================
 * IDS CRM Web - Bulletins for the fault codes being typed
 * =============================================================================
 *
 * Phase 3 of engineer bulletins, the reason they exist (Gareth 2026-09-11):
 * "The bulletin header should have a seperate non compulsory field for fault
 * codes... if these are entered on log work, the link to this bulletin can
 * appear under neat."
 *
 * Shown under the Fault codes box in Log Work: PUBLISHED bulletins sharing any
 * of the codes, newest first. A replaced bulletin is left out, because its
 * replacement carries the current fix.
 *
 * Informational and never in the way:
 *  - it waits until typing pauses, and ignores an answer for codes that have
 *    since changed, so a slow reply never shows the wrong fix;
 *  - it shows nothing if the lookup fails, because a weak signal in a parlour
 *    must not get between an engineer and saving their log;
 *  - a bulletin opens IN PLACE (BulletinPeek), not in a new tab. From IDS CRM
 *    on an iPhone home screen a new tab opens in Safari without the app's
 *    sign-in (review 2026-09-14), and in place the half-written log is kept.
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { parseFaultCodes } from '../utils/faultCodes';
import BulletinPeek from './BulletinPeek';
import './RelatedBulletins.css';

export const RELATED_LIMIT = 5;
/** "Show all" lists up to this many, newest first. */
export const RELATED_ALL_LIMIT = 50;
export const RELATED_DEBOUNCE_MS = 300;

const EMPTY = { key: '', rows: [], total: 0 };

function RelatedBulletins({ codes, authFetch }) {
  // Keyed on the normalised list: a new array holding the same codes (every
  // render of the form makes one) is not a new lookup.
  const codesKey = parseFaultCodes(codes).join(',');
  const [result, setResult] = useState(EMPTY);
  const [expanded, setExpanded] = useState(false);
  const [peekId, setPeekId] = useState(null);
  const openerRef = useRef(null);

  // New codes start again from the short list.
  useEffect(() => {
    setExpanded(false);
  }, [codesKey]);

  useEffect(() => {
    if (!codesKey) {
      setResult(EMPTY);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          status: 'published',
          fault_codes: codesKey,
          limit: String(expanded ? RELATED_ALL_LIMIT : RELATED_LIMIT)
        });
        const response = await authFetch(`/api/v1/bulletins?${params}`);
        if (!response.ok) throw new Error(`Bulletin lookup failed (${response.status})`);
        const body = await response.json();
        if (cancelled) return;
        const rows = Array.isArray(body.data) ? body.data : [];
        const total = body.pagination && Number.isFinite(Number(body.pagination.total))
          ? Number(body.pagination.total)
          : rows.length;
        setResult({ key: codesKey, rows, total });
      } catch (error) {
        if (!cancelled) setResult({ key: codesKey, rows: [], total: 0 });
      }
    }, RELATED_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authFetch, codesKey, expanded]);

  const openPeek = (event, bulletinId) => {
    openerRef.current = event.currentTarget;
    setPeekId(bulletinId);
  };

  const closePeek = useCallback(() => {
    setPeekId(null);
    // Back to the bulletin that was opened, so keyboard focus is not dropped.
    if (openerRef.current) openerRef.current.focus();
  }, []);

  // Only an answer for the codes on screen now is shown.
  const showing = Boolean(codesKey) && result.key === codesKey && result.rows.length > 0;
  const typed = new Set(codesKey.split(','));
  const { rows, total } = result;
  const count = Math.max(total, rows.length);
  const summary = showing
    ? `${count} bulletin${count === 1 ? '' : 's'} for ${typed.size === 1 ? 'this fault code' : 'these fault codes'}`
    : '';

  return (
    <>
      {/* Always on the page, so a screen reader announces the count when it
          changes; the list itself is not read out over the typing. */}
      <span className="sr-only" role="status" aria-live="polite">{summary}</span>

      {showing && (
        <div className="related-bulletins">
          <span className="related-bulletins-heading">{`${summary}:`}</span>
          <ul>
            {rows.map(row => {
              const matched = parseFaultCodes(row.fault_codes).filter(code => typed.has(code));
              return (
                <li key={row.bulletin_id}>
                  <button
                    type="button"
                    className="related-bulletins-link"
                    onClick={event => openPeek(event, row.bulletin_id)}
                  >
                    <span className="related-bulletins-title">{row.title}</span>
                    {matched.length > 0 && (
                      <span className="related-bulletins-meta">{matched.join(', ')}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {count > rows.length && !expanded && (
            <button type="button" className="related-bulletins-all" onClick={() => setExpanded(true)}>
              {`Show all ${count}`}
            </button>
          )}
          {expanded && count > rows.length && (
            <span className="related-bulletins-note">{`Showing the newest ${rows.length} of ${count}.`}</span>
          )}
        </div>
      )}

      {peekId && <BulletinPeek bulletinId={peekId} authFetch={authFetch} onClose={closePeek} />}
    </>
  );
}

export default RelatedBulletins;
